import { Module } from 'vuex';
import loginApi from '_c/business/api/login';
import { RootState } from '@/store';
import userApi from '_c/business/api/user';
import { logger } from '_c/util/logger';
import { updateUin } from '_c/util/aegis';
import { UserManager } from '_c/business/manager/user';
import { ValidateInviteCodeRsp } from '_c/typings/proto';
import { logoutAndClearUserInfo } from '@/business/util';

let loginInfo: LoginInfo;
let showAddDesktopCount: number;

function setLocalStorage(info: LoginInfo|undefined) {
  const userLoginInfo = JSON.stringify(info);
  localStorage.setItem('loginInfo', userLoginInfo);
  const uid = info?.uid;
  if (uid) updateUin(uid);
}

if (localStorage.getItem('loginInfo')) {
  loginInfo = JSON.parse(localStorage.getItem('loginInfo') as string);
  loginInfo.hasInviteCodeDone = loginInfo.uid !== '0';
  UserManager.getInstance().setCurrentUid(loginInfo.uid ?? '0');
}

if (localStorage.getItem('showAddDesktopCount')) {
  showAddDesktopCount = Number(localStorage.getItem('showAddDesktopCount'));
}

const store: Module<LoginStoreState, RootState> = {
  namespaced: true,

  state: () => ({
    loginInfo,
    showAddDesktopCount,
    isShowLogoutPopup: false,
  }),

  mutations: {
    updateLoginInfo(state, loginInfo) {
      state.loginInfo = loginInfo;
      if (loginInfo?.uid) UserManager.getInstance().setCurrentUid(loginInfo.uid);
      setLocalStorage(state.loginInfo);
    },
    updateLoginUid(state, uid) {
      state.loginInfo = {
        ...state.loginInfo as LoginInfo,
        uid,
      };
      UserManager.getInstance().setCurrentUid(uid);
      setLocalStorage(state.loginInfo);
    },
    updateIMSig(state, IMUserSig) {
      state.loginInfo = {
        ...state.loginInfo as LoginInfo,
        IMUserSig,
      };
      setLocalStorage(state.loginInfo);
    },
    updateInviteCodeIsValidate(state, codeValidateRsp: ValidateInviteCodeRsp) {
      state.loginInfo = {
        ...state.loginInfo as LoginInfo,
        hasInviteCodeDone: (codeValidateRsp.ret === 0),
      };
      setLocalStorage(state.loginInfo);
    },
    updateAddDesktopCount(state, count) {
      state.showAddDesktopCount = count;
      localStorage.setItem('showAddDesktopCount', count);
    },
    updateShowLogoutPopup(state, show: boolean) {
      state.isShowLogoutPopup = show;
    },
  },

  actions: {
    async getLoginInfo(
      store,
      { phoneNum, codeNum, type }: TGetLoginInfoParams,
    ): Promise<LoginInfo> {
      // 如果state已经有了,就直接返回
      if (store.state.loginInfo) {
        return store.state.loginInfo;
      }
      const loginRes = await loginApi.login(phoneNum, codeNum, type);
      const loginInfo: LoginInfo = {
        luUid: loginRes.luUid,
        luAppid: loginRes.luAppid,
        luSessionKey: loginRes.luSessionKey,
        loginType: loginRes.loginType,
        uid: loginRes.loginCallbackInfo.uid,
        bindPhone: loginRes.loginCallbackInfo.bindPhone,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        IMUserSig: loginRes.loginCallbackInfo.iMUserSig,
        inviteEnable: loginRes.loginCallbackInfo.inviteConfig.inviteEnable,
        inviteRequire: loginRes.loginCallbackInfo.inviteConfig.inviteRequire,
        inviteText: loginRes.loginCallbackInfo.inviteConfig.inviteText,
        hasInviteCodeDone: loginRes.loginCallbackInfo.uid !== '0',
      };
      store.commit('updateLoginInfo', loginInfo);
      return loginInfo;
    },
    async createUser(
      store,
      {
        uid,
        nickname,
        avatar,
        gender,
        userType,
        num,
        userStatus,
        code,
      }: TGePersonalInfoParams,
    ) {
      try {
        const res = await userApi.postCreateUser(
          uid,
          nickname,
          avatar,
          gender,
          userType,
          num,
          userStatus,
          code,
        );
        store.commit('updateLoginUid', res.userInfoBase.uid);
        return res;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async logout(store) {
      store.commit('updateLoginInfo', undefined);
      logoutAndClearUserInfo();
    },
    async getIMSig(store) {
      try {
        const res = await userApi.enterH5();
        store.commit('updateIMSig', res.imUserSig);
        return res;
      } catch (error) {
        logger.error(error, '获取imsig错误');
        return Promise.reject(error);
      }
    },
    async getInviteCodeIsValidate(store, { codeNum }) {
      try {
        const res = await loginApi.validateInviteCode(codeNum);
        store.commit('updateInviteCodeIsValidate', res);
        return res;
      } catch (error) {
        logger.error(error, '邀请码校验错误');
        return Promise.reject(error);
      }
    },
    async getAddDesktopCount(store, isClick) {
      try {
        const res = await loginApi.generateHTMLCount(isClick);
        store.commit('updateAddDesktopCount', res.count);
        return res;
      } catch (error) {
        logger.error(error, '获取getAddDesktopCount错误');
        return Promise.reject(error);
      }
    },
  },

  getters: {},
};

export default store;

export interface LoginStoreState {
  loginInfo?: LoginInfo;
  showAddDesktopCount?: number;
  isShowLogoutPopup?: boolean;
}

export interface LoginInfo {
  luUid: string;
  /** lu登录态token */
  luSessionKey: string;
  /** 登录类型 */
  loginType: number;
  /** lu appid */
  luAppid: string;
  /** 业务用户ID */
  uid: string;
  /** 腾讯云IM的UserSig, uid非0时会返回 */
  IMUserSig: string;
  /** 是否绑定了手机号 */
  bindPhone: boolean;
  /** 是否显示邀请码 */
  inviteEnable: boolean;
  /** 邀请码是否必要 */
  inviteRequire: boolean;
  /** 邀请码文案 */
  inviteText: string;
  /** 是否已经校验完邀请码 */
  hasInviteCodeDone: boolean;
}

export type TGetLoginInfoParams = {
  phoneNum: string;
  codeNum: string;
  type: number;
};
export type TGePersonalInfoParams = {
  uid: string;
  nickname: string;
  avatar: string;
  gender: number;
  userType: number;
  num: string;
  userStatus: number;
  code: string;
};
